import React, { useState, useEffect, useRef } from 'react';
import { colorPrimario } from '../../styles/colors';
import './Calendar7.styles.css';
import { formatHourWithoutSeconds } from '../../helpers/dates.helper';
 
// Constantes configurables
export const CALENDAR_CONFIG = {
  TIME_SLOT_MINUTES: 15, // Intervalo de tiempo en minutos
  START_HOUR: 0, // Hora de inicio (0 = 12 AM)
  END_HOUR: 23, // Hora de fin (23 = 11 PM)
  DEFAULT_VIEW_HOUR: 8, // Hora por defecto al cargar (8 AM)
  SLOT_HEIGHT: 20, // Altura de cada slot en píxeles
} as const;
 
// Constantes derivadas
const TIME_SLOT_MINUTES = CALENDAR_CONFIG.TIME_SLOT_MINUTES;
const START_HOUR = CALENDAR_CONFIG.START_HOUR;
const END_HOUR = CALENDAR_CONFIG.END_HOUR;
const DEFAULT_VIEW_HOUR = CALENDAR_CONFIG.DEFAULT_VIEW_HOUR;
const SLOT_HEIGHT = CALENDAR_CONFIG.SLOT_HEIGHT;
const HOURS_PER_DAY = 24;
 
// Tipos
export interface Cita {
  id: number;
  dia: number; // 0 = Domingo, 1 = Lunes, ..., 6 = Sábado
  fecha: string;
  horaDesde: string; // Formato "HH:MM"
  horaHasta: string; // Formato "HH:MM"
  titulo: string;
  descripcion?: string;
  tipo: 'CITA' | 'DISPONIBILIDAD';
  colorFondo?: string;
  colorTexto?: string;
}
 
export interface SlotClickData {
  dia: number; // 0 = Domingo, 1 = Lunes, ..., 6 = Sábado
  hora: string; // Hora inicial en formato "HH:MM"
  horaFinal: string; // Hora final en formato "HH:MM" (hora inicial + TIME_SLOT_MINUTES)
  fecha: Date; // Fecha real del día seleccionado
  cita?: Cita; // Cita existente en el slot (si hay)
}

export interface Calendar7Props {
  citas?: Cita[];
  onSlotClick?: (data: SlotClickData) => void;
  onCitaClick?: (cita: Cita) => void;
  onFechaChange?: (fecha: Date) => void;
  className?: string;
  config?: Partial<typeof CALENDAR_CONFIG>;
}
 
export const Calendar7: React.FC<Calendar7Props> = ({
  citas = [],
  onSlotClick,
  onCitaClick,
  onFechaChange,
  className = '',
  config = {}
}) => {
  // Combinar configuración por defecto con la personalizada
  const finalConfig = { ...CALENDAR_CONFIG, ...config };
   
  // Constantes dinámicas basadas en la configuración
  const TIME_SLOT_MINUTES = finalConfig.TIME_SLOT_MINUTES;
  const START_HOUR = finalConfig.START_HOUR;
  const END_HOUR = finalConfig.END_HOUR;
  const DEFAULT_VIEW_HOUR = finalConfig.DEFAULT_VIEW_HOUR;
  const SLOT_HEIGHT = finalConfig.SLOT_HEIGHT;
 
  const [fechaActual, setFechaActual] = useState(new Date());
  const [scrollPosition, setScrollPosition] = useState(0);
  const calendarRef = useRef<HTMLDivElement>(null);
  const onFechaChangeRef = useRef(onFechaChange);
  const lastFechaActualRef = useRef<string>('');

  // Actualizar la referencia cuando cambia onFechaChange
  useEffect(() => {
	onFechaChangeRef.current = onFechaChange;
  }, [onFechaChange]);

  // Llamar a onFechaChange solo cuando fechaActual cambia realmente
  useEffect(() => {
	// Convertir fechaActual a string para comparar
	const fechaActualString = fechaActual.toISOString();
	
	// Solo llamar si la fecha realmente cambió (evita llamadas duplicadas)
	if (lastFechaActualRef.current !== fechaActualString) {
	  lastFechaActualRef.current = fechaActualString;
	  
	  if (onFechaChangeRef.current) {
		onFechaChangeRef.current(fechaActual);
	  }
	}
  }, [fechaActual])
 
  // Calcular el inicio de la semana (Lunes)
  const getWeekStart = (date: Date): Date => {
	const d = new Date(date);
	const day = d.getDay();
	const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Ajustar para que Lunes sea el primer día
	return new Date(d.setDate(diff));
  };
 
  // Generar array de días de la semana
  const getWeekDays = (weekStart: Date): Date[] => {
	const days: Date[] = [];
	for (let i = 0; i < 7; i++) {
	  const day = new Date(weekStart);
	  day.setDate(weekStart.getDate() + i);
	  days.push(day);
	}
	return days;
  };
 
  // Generar slots de tiempo
  const generateTimeSlots = (): string[] => {
	const slots: string[] = [];
	for (let hour = START_HOUR; hour <= END_HOUR; hour++) {
	  for (let minute = 0; minute < 60; minute += TIME_SLOT_MINUTES) {
		const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
		slots.push(timeString);
	  }
	}
	return slots;
  };
 
  // Convertir hora a minutos desde medianoche
  const timeToMinutes = (time: string): number => {
	const [hours, minutes] = time.split(':').map(Number);
	return hours * 60 + minutes;
  };
 
  // Convertir minutos a posición en píxeles
  const minutesToPixels = (minutes: number): number => {
	return (minutes / TIME_SLOT_MINUTES) * SLOT_HEIGHT; // SLOT_HEIGHT px por slot de 15 min
  };
 
  // Obtener todas las citas que abarcan un slot específico
  const getCitasEnSlot = (dia: number, timeSlot: string): Cita[] => {
	return citas.filter(cita => {
	  if (cita.dia !== dia) return false;
	   
	  const slotMinutes = timeToMinutes(timeSlot);
	  const citaInicio = timeToMinutes(cita.horaDesde);
	  const citaFin = timeToMinutes(cita.horaHasta);
	   
	  return slotMinutes >= citaInicio && slotMinutes < citaFin;
	});
  };
 
  // Verificar si una cita comienza en un slot específico
  const isCitaInicio = (cita: Cita, timeSlot: string): boolean => {
	return timeToMinutes(cita.horaDesde) === timeToMinutes(timeSlot);
  };
 
  // Calcular la duración de una cita en slots
  const getCitaDuracionSlots = (cita: Cita): number => {
	const inicio = timeToMinutes(cita.horaDesde);
	const fin = timeToMinutes(cita.horaHasta);
	return Math.ceil((fin - inicio) / TIME_SLOT_MINUTES);
  };
 
  // Verificar si un slot está ocupado
  const isSlotOcupado = (dia: number, timeSlot: string): boolean => {
	return getCitasEnSlot(dia, timeSlot).length > 0;
  };
 
  // Función auxiliar para calcular la hora final
  const calcularHoraFinal = (horaInicial: string): string => {
	const [horas, minutos] = horaInicial.split(':').map(Number);
	const totalMinutos = horas * 60 + minutos + TIME_SLOT_MINUTES;
	const nuevaHora = Math.floor(totalMinutos / 60);
	const nuevoMinuto = totalMinutos % 60;
	
	return `${nuevaHora.toString().padStart(2, '0')}:${nuevoMinuto.toString().padStart(2, '0')}`;
  };

  // Manejar click en slot
  const handleSlotClick = (dayIndex: number, timeSlot: string) => {
	const citasEnSlot = getCitasEnSlot(dayIndex, timeSlot);
	const cita = citasEnSlot[0]; // Tomar la primera cita si hay múltiples
	
	if (onSlotClick) {
	  // dayIndex es el índice en el array weekDays (0-6)
	  const fechaSeleccionada = weekDays[dayIndex];
	  const diaSemana = fechaSeleccionada.getDay(); // 0 = Domingo, 1 = Lunes, etc.
	  
	  const slotData: SlotClickData = {
		dia: diaSemana,
		hora: timeSlot,
		horaFinal: calcularHoraFinal(timeSlot),
		fecha: fechaSeleccionada,
		cita
	  };
	  
	  onSlotClick(slotData);
	}
  };
 
  // Manejar click en cita
  const handleCitaClick = (cita: Cita, event: React.MouseEvent) => {
	event.stopPropagation();
	if (onCitaClick) {
	  onCitaClick(cita);
	}
  };
 
  // Navegación de fechas
  const goToPreviousWeek = () => {
	const newDate = new Date(fechaActual);
	newDate.setDate(newDate.getDate() - 7);
	setFechaActual(newDate);
  };
 
  const goToNextWeek = () => {
	const newDate = new Date(fechaActual);
	newDate.setDate(newDate.getDate() + 7);
	setFechaActual(newDate);
  };
 
  const goToToday = () => {
	setFechaActual(new Date());
  };
 
  // Scroll inicial a la hora configurada
  useEffect(() => {
	if (calendarRef.current) {
	  const defaultScrollPosition = minutesToPixels(timeToMinutes(`${DEFAULT_VIEW_HOUR}:00`));
	  calendarRef.current.scrollTop = defaultScrollPosition;
	  setScrollPosition(defaultScrollPosition);
	}
  }, [DEFAULT_VIEW_HOUR, TIME_SLOT_MINUTES, SLOT_HEIGHT]);
 
  // Manejar scroll
  const handleScroll = () => {
	if (calendarRef.current) {
	  setScrollPosition(calendarRef.current.scrollTop);
	}
  };
 
  const weekStart = getWeekStart(fechaActual);
  const weekDays = getWeekDays(weekStart);
  const timeSlots = generateTimeSlots();
 
  const dayNames = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
  const monthNames = [
	'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
	'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];
 
  return (
	<div className={`calendar7-container ${className}`}>
	  {/* Header con selector de fecha */}
	  <div className="calendar7-header">
		<div className="calendar7-date-controls">
		  <button 
			className="calendar7-nav-button" 
			onClick={goToPreviousWeek}
			title="Semana anterior"
		  >
			←
		  </button>
		  <button 
			className="calendar7-today-button" 
			onClick={goToToday}
			title="Ir a hoy"
		  >
			Hoy
		  </button>
		  <button 
			className="calendar7-nav-button" 
			onClick={goToNextWeek}
			title="Semana siguiente"
		  >
			→
		  </button>
		</div>
		<div className="calendar7-date-display">
		  <h2>
			{weekDays[0].getDate()} - {weekDays[6].getDate()} de {monthNames[weekDays[0].getMonth()]} {weekDays[0].getFullYear()}
		  </h2>
		</div>
	  </div>
 
	  {/* Calendario principal */}
	  <div className="calendar7-main">
		{/* Grid unificado con columna de horas incluida */}
		<div className="calendar7-grid-container" ref={calendarRef} onScroll={handleScroll}>
		  <div className="calendar7-grid">
			{/* Header de días (incluyendo columna de horas) */}
			<div className="calendar7-days-header">
			  <div className="calendar7-time-header">Hora</div>
			  {weekDays.map((day, index) => (
				<div key={index} className="calendar7-day-header">
				  <div className="calendar7-day-name">{dayNames[day.getDay()]}</div>
				  <div className="calendar7-day-number">{day.getDate()}</div>
				</div>
			  ))}
			</div>
 
			{/* Slots de tiempo (incluyendo columna de horas) */}
			<div className="calendar7-time-slots">
			  {timeSlots.map((timeSlot, timeIndex) => {
				const [hour] = timeSlot.split(':');
				const isHourMark = parseInt(timeSlot.split(':')[1]) === 0;
				 
				return (
				  <div key={timeSlot} className={`calendar7-time-row ${isHourMark ? 'calendar7-hour-mark' : ''}`}>
					{/* Columna de horas */}
					<div className="calendar7-time-slot">
					  {isHourMark && (
						<span className="calendar7-time-label">
						  {parseInt(hour) === 0 ? '12 AM' : 
						   parseInt(hour) < 12 ? `${parseInt(hour)} AM` :
						   parseInt(hour) === 12 ? '12 PM' : 
						   `${parseInt(hour) - 12} PM`}
						</span>
					  )}
					</div>
					 
					{/* Columnas de días */}
					{weekDays.map((day, dayIndex) => {
					  const citasEnSlot = getCitasEnSlot(day.getDay(), timeSlot);
					  const isOcupado = isSlotOcupado(day.getDay(), timeSlot);
					   
					  // Obtener todas las citas que empiezan en este slot y ordenarlas
					  // DISPONIBILIDAD primero (z-index menor), luego CITA (z-index mayor)
					  const citasParaRenderizar = citasEnSlot
						.filter(cita => isCitaInicio(cita, timeSlot))
						.sort((a, b) => {
						  // DISPONIBILIDAD va primero (renderiza primero = debajo)
						  if (a.tipo === 'DISPONIBILIDAD' && b.tipo === 'CITA') return -1;
						  if (a.tipo === 'CITA' && b.tipo === 'DISPONIBILIDAD') return 1;
						  return 0;
						});
					   
					  return (
						<div
						  key={`${dayIndex}-${timeIndex}`}
						  className={`calendar7-slot ${isOcupado ? 'calendar7-slot-ocupado' : 'calendar7-slot-vacio'}`}
						  onClick={() => handleSlotClick(dayIndex, timeSlot)}
						  title={isOcupado ? `Ocupado: ${citasEnSlot[0]?.titulo}` : `Libre: ${timeSlot}`}
						>
						  {citasParaRenderizar.map((cita, citaIndex) => (
							<div
							  key={`cita-${cita.id}-${citaIndex}`}
							  className={`calendar7-cita calendar7-cita-${cita.tipo.toLowerCase()}`}
							  style={{ 
								backgroundColor: cita.colorFondo || colorPrimario,
								height: `${getCitaDuracionSlots(cita) * SLOT_HEIGHT - 4}px`,
								zIndex: cita.tipo === 'CITA' ? 10 : 1
							  }}
							  onClick={(e) => handleCitaClick(cita, e)}
							  title={`${cita.titulo} - ${cita.horaDesde} a ${cita.horaHasta}`}
							>
							  <div style={{ color: cita.colorTexto || 'white' }} className="calendar7-cita-titulo">{cita.titulo} -- {formatHourWithoutSeconds(cita.horaDesde)} a {formatHourWithoutSeconds(cita.horaHasta)}</div>
							  {/* <div style={{ color: cita.colorTexto || 'white' }} className="calendar7-cita-hora">
								{cita.horaDesde} - {cita.horaHasta}
							  </div> */}
							</div>
						  ))}
						</div>
					  );
					})}
				  </div>
				);
			  })}
			</div>
		  </div>
		</div>
	  </div>
	</div>
  );
};
